import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import {
  clearAuth,
  getSessionExpiresAt,
  getToken,
  setSessionExpiresAt,
  setToken,
} from "@/services/authStorage";
import { toApiPattern, track } from "@/lib/analytics";

const baseURL =
  typeof import.meta.env.VITE_API_URL === "string" &&
  import.meta.env.VITE_API_URL.length > 0
    ? import.meta.env.VITE_API_URL.replace(/\/$/, "")
    : "http://localhost:3001";

export const apiBaseUrl = baseURL;

/** Renew access token this many ms before stored/JWT expiry. */
const REFRESH_SKEW_MS = 5 * 60 * 1000;

export const apiClient = axios.create({
  baseURL,
  // REQUIRED for the Magig funnel, not for auth: auth rides a Bearer header,
  // but `signup_completed` / `login_completed` are reported server-side and
  // MagigService drops the event unless the visitor's `_mag_anon` cookie
  // reaches it (see its report()). The cookie is set on .fiscalo.uy, and
  // app.fiscalo.uy -> api.fiscalo.uy is cross-ORIGIN, so axios' default
  // (withCredentials: false) stripped it from every /auth/register and
  // /auth/login POST — making the password path report no conversions at all
  // while SSO, a top-level navigation that carries cookies regardless, did.
  //
  // Works because bootstrap.ts already sets credentials: true and prod
  // CORS_ORIGIN is currently the explicit origin https://app.fiscalo.uy —
  // a CONFIG fact, not an invariant: with CORS_ORIGIN unset that same code
  // falls back to `origin: true`, which reflects any caller's origin and
  // would now accept credentialed cross-origin calls. Keep CORS_ORIGIN set.
  //
  // No account-takeover surface either way: the access token is a Bearer
  // header read from localStorage, which cross-origin JS cannot reach. The
  // cookies this sends are the Magig visitor id and the SSO state/PKCE pair
  // — and those are read on the OAuth callback NAVIGATION, which carried
  // them before this change too, so nothing new is exposed to XHR.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

function decodeJwtExpMs(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = JSON.parse(atob(padded)) as { exp?: number };
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

function getEffectiveExpiryMs(): number | null {
  const stored = getSessionExpiresAt();
  if (stored) {
    const t = new Date(stored).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const token = getToken();
  if (!token) return null;
  return decodeJwtExpMs(token);
}

/**
 * Ceiling for the two auth-path requests, which are small authenticated reads
 * that normally return in well under a second. 20s is far outside legitimate
 * latency even on a bad mobile connection.
 *
 * It exists because axios defaults `timeout` to 0 — wait forever — and a
 * stalled auth request does not fail, it simply never settles. That turns the
 * whole portal into a permanent spinner with NOTHING in the console, since a
 * pending request never produces a rejection to log. It is deliberately not a
 * global default on apiClient: the export and DGI-automation endpoints can
 * legitimately run long, and capping those without measuring them first would
 * trade a hang for spurious failures in the most fragile flows we have.
 */
export const AUTH_REQUEST_TIMEOUT_MS = 20_000;

let refreshPromise: Promise<boolean> | null = null;

/**
 * Calls POST /auth/session/refresh with the current Bearer token (standalone axios, no interceptors).
 * Serialized so concurrent 401s share one refresh.
 */
export async function tryRefreshAccessToken(): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  if (!refreshPromise) {
    refreshPromise = (async (): Promise<boolean> => {
      try {
        const current = getToken();
        if (!current) return false;
        const { data } = await axios.post<{
          accessToken: string;
          expiresAt: string;
        }>(`${baseURL}/auth/session/refresh`, {}, {
          headers: {
            Authorization: `Bearer ${current}`,
            "Content-Type": "application/json",
          },
          // The request interceptor awaits this before every non-auth call,
          // and refreshPromise memoises the in-flight one — so without a
          // ceiling a single stalled refresh deadlocks every concurrent
          // request in the app, not just this one.
          timeout: AUTH_REQUEST_TIMEOUT_MS,
        });
        setToken(data.accessToken);
        setSessionExpiresAt(data.expiresAt);
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

/**
 * If the session is within REFRESH_SKEW_MS of expiry, refresh (no-op otherwise).
 */
export async function proactiveSessionRefreshIfDue(): Promise<void> {
  if (!getToken()) return;
  const expMs = getEffectiveExpiryMs();
  if (expMs == null) return;
  if (expMs - Date.now() > REFRESH_SKEW_MS) return;
  await tryRefreshAccessToken();
}

function isSkipProactiveRefreshUrl(url: string): boolean {
  return (
    url === "/auth/session/refresh" ||
    url === "/auth/login" ||
    url === "/auth/login-link" ||
    url === "/auth/verify"
  );
}

/**
 * Per-request tenant scope. When an accountant is viewing a specific
 * client (request detail, etc.) without flipping their active tenant,
 * the page sets this to the client's tenantId and the interceptor
 * stamps `X-Tenant-Id` on every API call. The backend's AuthGuard
 * validates the membership and uses the override as activeTenantId
 * for THAT request only — JWT/session stay untouched.
 *
 * Module-level on purpose: setters are call sites (page mount /
 * unmount), readers are every API call. React context would force the
 * interceptor to be inside the React tree.
 */
let viewingTenantId: string | null = null;

export function setViewingTenantId(tenantId: string | null): void {
  viewingTenantId = tenantId;
}

export function getViewingTenantId(): string | null {
  return viewingTenantId;
}

apiClient.interceptors.request.use(async (config) => {
  const url = typeof config.url === "string" ? config.url : "";
  if (!isSkipProactiveRefreshUrl(url)) {
    await proactiveSessionRefreshIfDue();
  }
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (viewingTenantId) {
    config.headers["X-Tenant-Id"] = viewingTenantId;
  }
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

/**
 * Public payment landing routes — the buyer here is the accountant's
 * client, not authed in the portal. If the request that 401'd happened
 * while on one of these routes (e.g. AuthProvider's initial getSession
 * fired with a stale tester cookie), clear auth but stay on the page
 * instead of force-navigating to /login. Otherwise an accountant
 * testing the flow in their own browser bounces away from the
 * thank-you screen instead of seeing it.
 */
const PUBLIC_PAYMENT_PATHS = new Set([
  "/pay/thank-you",
  "/pay/link-not-available",
  // /pay/dlocal-return is dual-audience: the authed tenant lands here
  // from their own checkout, and an unauthed external buyer lands here
  // when dLocal subscription plans ignore our per-request success_url
  // and use the dashboard default. Stay-on-page on 401 so the page
  // itself can decide which audience it's serving.
  "/pay/dlocal-return",
]);

function isCurrentPathPublicPayment(): boolean {
  if (typeof window === "undefined") return false;
  return PUBLIC_PAYMENT_PATHS.has(window.location.pathname);
}

/**
 * Emits `api.failed` for a failed response.
 *
 * Called from the response interceptor ABOVE its 401 early-return: placed
 * below, every auth failure would go uncounted, and those are exactly the ones
 * worth seeing. This is the hook that would have surfaced
 * GET /invoices/recibidas/export returning 500 for 5.5 months behind a live
 * Settings button.
 *
 * Records only method, status and a sanitised route pattern. The response body
 * is deliberately never read — it can carry fiscal data. `/events` is excluded
 * so a failing ingest call cannot describe its own failure and re-flush.
 */
function recordFailedRequest(error: AxiosError, requestUrl: string): void {
  // Same reasoning as recordPerformedAction, and the stakes are higher: this
  // runs above the 401 refresh branch, so a throw here would break session
  // renewal for every user.
  try {
    if (requestUrl === "/events") return;
    track("api.failed", {
      method: (error.config?.method ?? "get").toUpperCase(),
      status: error.response?.status ?? 0,
      route: toApiPattern(requestUrl),
    });
  } catch {
    // Never let telemetry interfere with error handling.
  }
}

/**
 * Emits `action.performed` for a successful mutation.
 *
 * Captured centrally rather than at the ~204 useMutation call sites, for the
 * same reason `api.failed` is: a hook here cannot rot. Per-call-site tracking
 * has to be remembered by whoever adds the next feature, and the one place it
 * gets forgotten is a new feature — exactly the thing we most want to know is
 * being used.
 *
 * Non-GET only. A mutation is by definition something a user chose to do,
 * whereas GETs are dominated by background refetches and would drown the
 * signal. Method + sanitised route is enough to identify the action; the
 * request body is never read, since it carries the invoice itself.
 */
function recordPerformedAction(response: AxiosResponse): void {
  // Wrapped whole. `track` swallows its own errors, but the ARGUMENTS to it —
  // toApiPattern here — are evaluated first, outside that guard. A throw would
  // escape into the interceptor and turn every successful mutation into a
  // rejected promise, i.e. invoicing would appear to fail because of
  // analytics. Nothing on this path is worth that risk.
  try {
    const method = (response.config?.method ?? "get").toUpperCase();
    if (method === "GET") return;
    const url =
      typeof response.config?.url === "string" ? response.config.url : "";
    if (url === "/events") return;
    track("action.performed", { method, route: toApiPattern(url) });
  } catch {
    // Telemetry must never break a request that actually succeeded.
  }
}

apiClient.interceptors.response.use(
  (response) => {
    recordPerformedAction(response);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const requestUrl =
      typeof originalRequest?.url === "string" ? originalRequest.url : "";

    recordFailedRequest(error, requestUrl);

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    if (
      requestUrl === "/auth/login" ||
      requestUrl === "/auth/login-link" ||
      requestUrl === "/auth/verify" ||
      requestUrl === "/auth/verify-root-invite" ||
      requestUrl === "/auth/sso/exchange"
    ) {
      return Promise.reject(error);
    }

    if (requestUrl === "/auth/session/refresh") {
      clearAuth();
      if (!isCurrentPathPublicPayment()) {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    if (!originalRequest._retry) {
      originalRequest._retry = true;
      const refreshed = await tryRefreshAccessToken();
      if (refreshed) {
        const nextToken = getToken();
        if (nextToken) {
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${nextToken}`;
          return apiClient(originalRequest);
        }
      }
    }

    clearAuth();
    if (!isCurrentPathPublicPayment()) {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);
